<template>
  <nav
    v-if="hasMultiplePages"
    class="pagination"
    role="navigation"
    aria-label="Seitennummerierung"
    aria-describedby="pagination-desc"
  >
    <span
      id="pagination-desc"
      class="sr-only"
    >
      {{ paginationLabel }}
    </span>
    <LinkButton
      :tag="hasPrevPage ? linkTag : 'span'"
      :disabled="!hasPrevPage"
      :tabindex="hasPrevPage ? undefined : -1"
      :to="emitOnlyClick ? undefined : prevPageUrl"
      @click="hasPrevPage ? emitClick(prevPageUrl) : undefined"
    >
      <Arrow
        direction="left"
        tag="span"
      />
      Zurück <span class="sr-only">{{ prevPageAriaLabel }}</span>
    </LinkButton>

    <LinkPaginator
      class="pagination__paginator"
      :current-page="currentPage"
      :emit-only-click="emitOnlyClick"
      :total-pages="totalPages"
      :url="url"
      aria-label-prefix="Seite"
      :current-page-aria-label="currentPageAriaLabel"
      @click="emitClick"
    />

    <SelectPaginator
      class="pagination__selectbox"
      :current-page="currentPage"
      :emit-only-click="emitOnlyClick"
      :total-pages="totalPages"
      :url="url"
      aria-label-prefix="Seite"
      :current-page-aria-label="currentPageAriaLabel"
      @click="emitClick"
    />

    <LinkButton
      :tag="hasNextPage ? linkTag : 'span'"
      :disabled="!hasNextPage"
      :tabindex="hasNextPage ? undefined : -1"
      :to="emitOnlyClick ? undefined : nextPageUrl"
      @click="hasNextPage ? emitClick(nextPageUrl) : undefined"
    >
      Weiter <span class="sr-only">{{ nextPageAriaLabel }}</span>
      <Arrow
        direction="right"
        tag="span"
      />
    </LinkButton>
  </nav>
</template>

<script setup lang="ts">
import LinkPaginator from '~/components/link-paginator.vue';
import { Arrow, LinkButton } from '@i22team/td-longtail-vue-components';

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  url: (page: number) => string;
  emitOnlyClick?: boolean;
};

const props = withDefaults(defineProps<PaginationProps>(), {
  emitOnlyClick: false,
});

const emit = defineEmits<{ click: [target: string | number] }>();

const hasMultiplePages = computed(() => props.totalPages > 1);

const hasPrevPage = computed(() => props.currentPage > 1);
const hasNextPage = computed(() => props.currentPage < props.totalPages);

const prevPageUrl = computed(() => props.url(props.currentPage - 1));

const nextPageUrl = computed(() => props.url(props.currentPage + 1));

const paginationLabel = computed(() => `Seite ${props.currentPage} von ${props.totalPages}.`);
const prevPageAriaLabel = computed(() =>
  hasPrevPage.value
    ? `zur vorherigen Seite, Seite ${props.currentPage - 1} .`
    : '. Keine vorherige Seite verfügbar.'
);
const nextPageAriaLabel = computed(() =>
  hasNextPage.value
    ? `zur nächsten Seite, Seite ${props.currentPage + 1} .`
    : '. Keine nächste Seite verfügbar.'
);
const currentPageAriaLabel = computed(() => `Aktuelle Seite, Seite ${props.currentPage}.`);

const linkTag = computed(() => (props.emitOnlyClick ? 'button' : 'nuxt-link'));

function emitClick(target: string | number | undefined) {
  if (target === undefined) return;
  emit('click', target);
}
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  grid-gap: $grid-base * 2;
  user-select: none;

  --arrow-position: relative;
  --arrow-width: 0.6em;
  --arrow-height: 0.6em;

  @include mq($mq-sm) {
    grid-gap: $grid-base * 3;
  }

  &__paginator {
    @include mq(false, $mq-sm - 1) {
      display: none;
    }
  }

  &__selectbox {
    @include mq($mq-sm) {
      display: none;
    }
  }
}
</style>
